/**
 * H360 Retention Stability Calculator
 * Version: 1.0.0
 * Owner: H360 • Powered by TravelGrowth AI
 * 
 * Deterministic, modular calculator that quantifies the hidden cost of workforce instability
 * and models savings from retention uplift + time-to-fill reduction.
 */

// Constants
const WORKDAYS_PER_YEAR = 260;
const DAYS_PER_WEEK = 5;
const PRODUCTIVITY_FACTOR_VACANCY = 0.60;
const PRODUCTIVITY_FACTOR_ABSENT = 0.40;
const TRAINING_RAMP_PCT_SALARY = 0.15;
const REVENUE_UNREALIZED_PCT = 0.25;
const ABSENTEEISM_IMPROVEMENT_PCT = 0.07;

/**
 * Calculate brand penalty multiplier based on turnover rate
 */
function getBrandPenaltyMultiplier(turnoverRatePct) {
  if (turnoverRatePct > 25) return 0.08;
  if (turnoverRatePct > 15) return 0.05;
  return 0.03;
}

/**
 * Main calculation function matching the specification
 */
export function calculateResults(inputs) {
  const {
    companySize,
    avgSalary,
    replaceTime,
    revenuePerEmployee,
    retentionUplift,
    turnoverRate,
    costPerHire,
    absenteeism,
    programCost,
    timeToFillReduction,
  } = inputs;

  // Derived values
  const salaryPerDay = avgSalary / WORKDAYS_PER_YEAR;
  const vacancyDays = replaceTime * DAYS_PER_WEEK;
  const turnoverCount = companySize * (turnoverRate / 100);

  // Cost component equations (baseline, before scenario)
  const turnoverCost = turnoverCount * costPerHire;
  
  const productivityLoss = 
    turnoverCount * 
    vacancyDays * 
    (salaryPerDay * PRODUCTIVITY_FACTOR_VACANCY);
  
  const absenteeismCost = 
    companySize * 
    (absenteeism / 100) * 
    WORKDAYS_PER_YEAR * 
    (salaryPerDay * PRODUCTIVITY_FACTOR_ABSENT);
  
  const trainingRampCost = 
    turnoverCount * 
    (avgSalary * TRAINING_RAMP_PCT_SALARY);
  
  const brandPenaltyMultiplier = getBrandPenaltyMultiplier(turnoverRate);
  const brandPenalty = 
    brandPenaltyMultiplier * 
    (turnoverCost + productivityLoss);
  
  const opportunityLoss = revenuePerEmployee
    ? turnoverCount * 
      vacancyDays * 
      (revenuePerEmployee / WORKDAYS_PER_YEAR) * 
      REVENUE_UNREALIZED_PCT
    : 0;

  const totalInstabilityCost = 
    turnoverCost + 
    productivityLoss + 
    absenteeismCost + 
    trainingRampCost + 
    brandPenalty + 
    opportunityLoss;

  // Scenario calculations (retention uplift + time-to-fill reduction)
  const reducedTurnoverCount = 
    turnoverCount * (1 - retentionUplift / 100);
  
  const savedHires = 
    (turnoverCount - reducedTurnoverCount) * costPerHire;
  
  const savedTraining = 
    (turnoverCount - reducedTurnoverCount) * 
    (avgSalary * TRAINING_RAMP_PCT_SALARY);
  
  const reducedVacancyDays = 
    vacancyDays * (1 - timeToFillReduction / 100);
  
  const baselineVacancyCost = 
    turnoverCount * 
    vacancyDays * 
    (salaryPerDay * PRODUCTIVITY_FACTOR_VACANCY);
  
  const scenarioVacancyCost = 
    reducedTurnoverCount * 
    reducedVacancyDays * 
    (salaryPerDay * PRODUCTIVITY_FACTOR_VACANCY);
  
  const savedVacancy = baselineVacancyCost - scenarioVacancyCost;
  
  const intlRecruitmentSavingsSubset = 
    savedHires + savedTraining + savedVacancy;
  
  const projectedSavings = Math.max(
    0, 
    intlRecruitmentSavingsSubset + (absenteeismCost * ABSENTEEISM_IMPROVEMENT_PCT)
  );
  
  const roiPct = (projectedSavings / Math.max(1, programCost)) * 100;

  // Return output matching the API contract schema
  return {
    totals: {
      totalInstabilityCost: Math.round(totalInstabilityCost),
      projectedSavings: Math.round(projectedSavings),
      roiPct: Math.min(roiPct, 300), // Cap at 300% for display
    },
    subsets: {
      intlRecruitmentSavingsSubset: Math.round(intlRecruitmentSavingsSubset),
    },
    breakdown: {
      turnoverCost: Math.round(turnoverCost),
      productivityLoss: Math.round(productivityLoss),
      absenteeismCost: Math.round(absenteeismCost),
      trainingRampCost: Math.round(trainingRampCost),
      brandPenalty: Math.round(brandPenalty),
      opportunityLoss: Math.round(opportunityLoss),
    },
    meta: {
      version: "1.0.0",
      assumptions: {
        productivityFactorVacancy: PRODUCTIVITY_FACTOR_VACANCY,
        productivityFactorAbsent: PRODUCTIVITY_FACTOR_ABSENT,
        trainingRampPctSalary: TRAINING_RAMP_PCT_SALARY,
        brandPenaltyMultiplier: brandPenaltyMultiplier,
        revenueUnrealizedPct: REVENUE_UNREALIZED_PCT,
        absenteeismImprovementPct: ABSENTEEISM_IMPROVEMENT_PCT,
      },
    },
  };
}
